import { useCallback, useContext, useMemo, useState } from 'react';
import appNavItemTypeCheckers from '../../../types/appNavItemTypeCheckers';
import FormInput from '../../../kingpin/forms/FormInput';
import Inputs from '../../../components/Inputs';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import getIdentifier from '../../../getIdentifier';
import STORE from '../../../store';

const WorkspaceTasksToggle = ({ workspace }: { workspace: V3.Workspace }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const hasTasks = useMemo(() => {
    return workspace.navigationSettings.sections.some((section) =>
      section.items.some(appNavItemTypeCheckers.isTasks),
    );
  }, [workspace.navigationSettings.sections]);
  const [isLoading, setIsLoading] = useState(false);

  const generateTasksSection = useCallback((): V3.Navigation.SideSection => {
    const tasksItem: V3.Navigation.Tasks = {
      isAppAdminOnly: false,
      label: 'Tasks',
      key: getIdentifier(),
      includeGoalTasks: true,
      includeScorecardTasks: true,
      type: 'Tasks',
    };

    return {
      key: getIdentifier(),
      title: {
        isEnabled: false,
      },
      isBottomBorderEnabled: true,
      items: [tasksItem],
      sectionSettings: {
        isPinnedSection: false,
        isPeriodFilteredSection: false,
      },
      isAppAdminOnly: false,
    };
  }, []);

  const addTasks = useCallback(() => {
    setIsLoading(true);
    const newWorkspace: V3.Workspace = {
      ...workspace,
      navigationSettings: {
        ...workspace.navigationSettings,
        sections: [
          workspace.navigationSettings.sections[0],
          generateTasksSection(),
          workspace.navigationSettings.sections[1],
        ],
      },
    };

    STORE.contentDistributions
      .getWorkspacesRef({
        accountId: selectedAccountId,
      })
      .doc(newWorkspace.id)
      .set(newWorkspace)
      .then(() => {
        setIsLoading(false);
      });
  }, [generateTasksSection, selectedAccountId, workspace]);

  const removeTasks = useCallback(() => {
    setIsLoading(true);
    const newWorkspace: V3.Workspace = {
      ...workspace,
      navigationSettings: {
        ...workspace.navigationSettings,
        sections: workspace.navigationSettings.sections.filter((section) => {
          return !section.items.some(appNavItemTypeCheckers.isTasks);
        }),
      },
    };

    STORE.contentDistributions
      .getWorkspacesRef({
        accountId: selectedAccountId,
      })
      .doc(newWorkspace.id)
      .set(newWorkspace)
      .then(() => {
        setIsLoading(false);
      });
  }, [selectedAccountId, workspace]);

  const onTasksToggled = useCallback(
    (newValue: boolean) => {
      if (newValue) {
        addTasks();
      } else {
        removeTasks();
      }
    },
    [addTasks, removeTasks],
  );

  return (
    <FormInput label={'Tasks'}>
      <Inputs.Toggle
        value={hasTasks}
        onChange={onTasksToggled}
        isDisabled={isLoading}
      />
    </FormInput>
  );
};

export default WorkspaceTasksToggle;
